import { all, call, fork, put, take, takeLatest, select } from 'redux-saga/effects';
import * as AccountServiceApi from '../../Apis/AccountServiceApi';
import * as G2WBrokerApi from '../../Apis/G2WBrokerApi';
import AppConstants from '../../AppConstants';
import {
  logout,
  setMixpanelLoginEventToBeSent,
  shouldMixpanelLoginEventBeSent
} from '../../Authentication/AuthenticationService';
import { AUTH, LOGIN, LOGOUT } from '../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import { trackAnalyticsFeature } from '../../Telemetry/Mixpanel/Features/analyticsTracker';
import { initMixpanelFromUser, reset as resetMixpanel } from '../../Telemetry/Mixpanel/mixpanelClient';
import User from '../../Model/User';
import { clearUserSessionId, setUserSessionId } from '../../Model/UserProperties';
import i18n from '../../Utils/i18n';
import { shutdownIntercomMessenger } from '../../Telemetry/Intercom/intercom';
import { forceLoginIfExpired, redirectToMyAccountPage, redirectToOldView } from '../Utils/RedirectionUtils';
import * as Actions from './UserActions';
import { G2S, UserSettings } from '../../Utils/Constants';
import { initializeAppcues } from '../../Utils/Appcues';
import { setUpPendo } from '../../Pendo';

export function* getUserWatcher() {
  yield takeLatest(Actions.GET_USER_REQUEST, getUserWorker);
}

export function* switchBackToOldViewWatcher() {
  yield takeLatest(Actions.SWITCH_BACK_TO_OLD_VIEW_REQUEST, switchBackToOldView);
}

export function* watchEventsAndDecideIfAppReady() {
  const successCheckEvents = [take(Actions.GET_USER_SUCCESS)];
  yield all(successCheckEvents);
  yield put({ type: Actions.APP_READY });
}

export function* logoutUser() {
  yield take(Actions.APP_LOGOUT_REQUEST);
  trackAnalyticsFeature(AUTH, null, LOGOUT);
  clearUserSessionId();
  if (AppConstants.INTERCOM_ENABLED) {
    shutdownIntercomMessenger();
  }
  resetMixpanel();
  yield logout();
}

export function* getUserWorker(action) {
  let user = null;
  let metadata = null;
  try {
    const { data: me } = yield call(AccountServiceApi.getMe);
    if (action.isAdmin) {
      const { data: admin } = yield call(G2WBrokerApi.adminCheck, action.accountKey);
      metadata = {
        accountKey: action.accountKey,
        userKey: me.id
      };
      user = new User(me, metadata);
      user.isAdmin = admin.admin;
      User.isAdmin = admin.admin;
    }
    if (me.entitlements && !me.entitlements.includes('g2w') && !(action.isAdmin && user.isAdmin)) {
      redirectToMyAccountPage();
    }
    if (!action.isAdmin) {
      const { data } = yield call(G2WBrokerApi.getMetadata);
      metadata = data;
      user = new User(me, metadata);
    }
    user.entitlements.push(G2S);
    yield put({ type: Actions.GET_USER_SUCCESS, payload: { user } });
  } catch (error) {
    yield put({ type: Actions.GET_USER_FAILURE, error });
    forceLoginIfExpired(error);
  }
  if (user) {
    setUserSessionId();
    i18n.changeLanguage(user.userLocaleShort);

    try {
      const { accountKey, userKey } = user;
      const { data: darkModeConfig } = yield call(AccountServiceApi.getUserConfig, accountKey, userKey);
      yield put({ type: Actions.GET_USER_DARK_MODE_CONFIG, payload: darkModeConfig.data });
    } catch (error) {
      yield put({ type: Actions.GET_USER_DARK_MODE_CONFIG, payload: false });
    }

    yield fork(getUserSettings, user);
    initMixpanelFromUser(user, metadata);

    if (shouldMixpanelLoginEventBeSent()) {
      trackAnalyticsFeature(AUTH, null, LOGIN);
      setMixpanelLoginEventToBeSent('false');
    }

    if (AppConstants.APPCUES_ENABLED) {
      initializeAppcues(user, metadata);
    }
    if (metadata.userKey) {
      setUpPendo(metadata);
    }
  }
}

function* getUserSettings(user) {
  try {
    const { accountKey, userKey } = user;
    const axiosResponse = yield call(G2WBrokerApi.getUserSettings, accountKey, userKey);
    yield put({
      type: Actions.GET_USER_SETTINGS_SUCCESS,
      payload: { entitlements: axiosResponse.data }
    });
  } catch (error) {
    yield put({ type: Actions.GET_USER_SETTINGS_FAILURE, error });
    forceLoginIfExpired(error);
  }
}

export function* switchBackToOldView() {
  try {
    const { accountKey, userKey } = yield select(state => state.reduced_user.user);
    yield call(G2WBrokerApi.updateUserSettings, accountKey, userKey, {
      [UserSettings.CLASSIC_FRONTEND_EXPERIENCE]: true,
      [UserSettings.CAN_TOGGLE_FRONTEND_EXPERIENCE]: true
    });
    redirectToOldView();
  } catch (e) {
    console.error('unable to switch back to old view');
  }
}

export default function* rootSaga() {
  yield all([
    fork(getUserWatcher),
    fork(watchEventsAndDecideIfAppReady),
    fork(logoutUser),
    fork(switchBackToOldViewWatcher)
  ]);
}



// WEBPACK FOOTER //
// ./src/Reducks/User/UserSagas.js